/**
 * This script creates a reusable size dropdown selector for a product page.
 * It can be instantiated multiple times for different locations on the page
 * while maintaining synchronization with the main product size selection.
 */

class SizeDropdown {
  /**
   * Creates a new size dropdown instance
   * @param {Object} config Configuration options
   * @param {string} config.containerId - DOM ID of the container where dropdown will be placed
   * @param {string} config.dropdownId - ID to assign to the new dropdown element
   * @param {string} config.labelText - Text for the dropdown label
   * @param {string} config.defaultText - Default text when no size is selected
   * @param {Function} config.onSizeChange - Optional callback when size changes
   */
  constructor(config) {
    this.containerId = config.containerId;
    this.dropdownId = config.dropdownId;
    this.labelText = config.labelText;
    this.defaultText = config.defaultText || 'Select a size';
    this.onSizeChange = config.onSizeChange;
    
    this.container = document.getElementById(this.containerId);
    if (!this.container) {
      console.warn(`Container with ID "${this.containerId}" not found.`);
      return;
    }

    this.productContainer = document.querySelector('.js-main-product [data-active-gender]');
    if (!this.productContainer) {
      console.warn('No productContainer found with [data-active-gender].');
      return;
    }

    this.render();
    this.attachEventListeners();
    this.populateDropdown();
  }

  /**
   * Creates and renders the dropdown HTML structure
   */
  render() {
    const dropdownHTML = `
      <div class="size-dropdown-wrapper">
        <label class="size-dropdown-label visually-hidden" for="${this.dropdownId}">${this.labelText}</label>
        <select id="${this.dropdownId}" class="select size-dropdown" name="size-dropdown"></select>
        <span class="size-dropdown-active visually-hidden">${this.defaultText}</span>
      </div>
    `;
    
    this.container.innerHTML = dropdownHTML;
    this.dropdown = document.getElementById(this.dropdownId);
    this.activeLabel = this.container.querySelector('.size-dropdown-active');
  }

  /**
   * Attaches event listeners to the dropdown and global events
   */
  attachEventListeners() {
    // Listen for changes to this dropdown
    this.dropdown.addEventListener('change', this.handleDropdownChange.bind(this));
    
    // Listen for global events to sync with main product size selector
    document.addEventListener('sizeScaleUpdated', this.populateDropdown.bind(this));
    document.addEventListener('sizeSelected', this.syncActiveSize.bind(this));
    document.addEventListener('genderUpdated', this.populateDropdown.bind(this));
  }

  /**
   * Populates the dropdown with size options
   */
  populateDropdown() {
    // Clear existing options
    this.dropdown.innerHTML = '';
    // Add a default option
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = this.defaultText;
    defaultOption.disabled = true;
    this.dropdown.appendChild(defaultOption);

    // Fetch current scale and product attributes
    const dropdownButton = document.getElementById('product-size-dropdown-button');
    const selectedLocation = (dropdownButton?.dataset.selectedLocation || 'US').toLowerCase();
    const activeGender = this.productContainer.getAttribute('data-active-gender');
    const productType = this.productContainer.getAttribute('data-product-type');

    const isGiftCardProduct = this.productContainer.hasAttribute('data-gift-card-product');
    const isMultiGenderProduct = this.productContainer.hasAttribute('data-multi-gender-product');
    const isUnisexProduct = this.productContainer.hasAttribute('data-unisex-product');

    // Determine the gender key for scale attributes
    let genderKey;
    if (activeGender === 'women') {
      genderKey = 'women';
    } else if (activeGender === 'men') {
      genderKey = 'men';
    } else {
      genderKey = null;
    }

    // Determine which data attribute to use for scaled sizes
    let scaleDataAttr;
    if (isGiftCardProduct) {
      scaleDataAttr = 'data-value';
    } else if (productType === 'Footwear' && genderKey) {
      scaleDataAttr = `data-size-${genderKey}-${selectedLocation}`;
    } else {
      scaleDataAttr = 'data-default-value';
    }

    // Gather currently visible size options
    const allSizes = Array.from(document.querySelectorAll('.js-main-product .size-inputs-wrapper .size_value'))
      .filter(valueEl => {
        const label = valueEl.closest('label');
        return label && label.style.display !== 'none';
      });

    let checkedOptionValue = null; // Track which option should be selected in the dropdown

    // Build dropdown options
    allSizes.forEach(valueEl => {
      const option = document.createElement('option');
      const labelValue = valueEl.textContent.trim();

      // Find the associated input element for this size
      const labelEl = valueEl.closest('label');
      let parentInput = null;
      if (labelEl) {
        const forValue = labelEl.getAttribute('for');
        parentInput = forValue ? document.getElementById(forValue) : null;
      }

      // Determine the actual scaled value to display
      const scaleValue = parentInput ? (parentInput.getAttribute(scaleDataAttr) || labelValue) : labelValue;
      option.value = scaleValue;

      // Format option text based on product type and attributes
      let optionText;
      if (isGiftCardProduct) {
        optionText = scaleValue; // Gift card uses the scale value directly
      } else if (isMultiGenderProduct || isUnisexProduct) {
        // Multi-gender/unisex: include gender and location, e.g. "women's EU 36"
        optionText = `${activeGender}'s ${selectedLocation.toUpperCase()} ${scaleValue}`;
      } else {
        // Normal product: "US 5"
        optionText = `${selectedLocation.toUpperCase()} ${scaleValue}`;
      }

      // 🔴 Append availability-related messages

      //const inventoryMsg = parentInput?.getAttribute('data-inventory-msz');

      const isSizeAvailable = parentInput?.getAttribute('data-available') !== 'false';
      
      const preorderMsg = parentInput?.getAttribute('data-preorder-msz');

      const messages = [];

      if (!isSizeAvailable) {
        messages.push('Sold Out');
      }

      if (preorderMsg) {
        messages.push(preorderMsg);
      }

      if (messages.length > 0) {
        optionText += ' - ' + messages.join(' / ');
      }

      option.textContent = optionText;

      this.dropdown.appendChild(option);

      // If this input is checked, remember it to set as selected later
      if (parentInput && parentInput.checked) {
        checkedOptionValue = scaleValue;
        this.activeLabel.textContent = optionText;
      }
    });

    // If we have a checked input, set the dropdown's value to it
    if (checkedOptionValue) {
      this.dropdown.value = checkedOptionValue;
    }
  }

  /**
   * Handles dropdown change events
   */
  handleDropdownChange() {

    const productSizes = Array.from(document.querySelectorAll('.js-main-product .size-inputs-wrapper input'));
    const dropdownButton = document.getElementById('product-size-dropdown-button');
    const selectedLocation = (dropdownButton?.dataset.selectedLocation || 'US').toLowerCase();
    const activeGender = this.productContainer.getAttribute('data-active-gender');
    const productType = this.productContainer.getAttribute('data-product-type');
    const isGiftCardProduct = this.productContainer.hasAttribute('data-gift-card-product');
    const isMultiGenderProduct = this.productContainer.hasAttribute('data-multi-gender-product');
    const isUnisexProduct = this.productContainer.hasAttribute('data-unisex-product');

    let genderKey;
    if (activeGender === 'women') {
      genderKey = 'women';
    } else if (activeGender === 'men') {
      genderKey = 'men';
    } else {
      genderKey = null;
    }

    let dataSize;
    if (isGiftCardProduct) {
      dataSize = 'data-value';
    } else if (productType === 'Footwear' && genderKey) {
      dataSize = `data-size-${genderKey}-${selectedLocation}`;
    } else {
      dataSize = 'data-default-value';
    }

    // Match the selected option to the correct input and trigger its change event
    const productSize = productSizes.find(input => {
      const attrVal = input.getAttribute(dataSize);
      return attrVal === this.dropdown.value;
    });

    if (productSize) {
      productSize.checked = true;
      productSize.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Update the active size text
      const selectedOption = this.dropdown.options[this.dropdown.selectedIndex];
      this.activeLabel.textContent = selectedOption.textContent;
      
      // Call the callback if provided
      if (typeof this.onSizeChange === 'function') {
        this.onSizeChange(this.dropdown.value, selectedOption.textContent);
      }
    }
  }

  /**
   * Syncs this dropdown with the main product size selector
   * @param {Event} e - The sizeSelected event
   */
  syncActiveSize(e) {
    if (e.detail && e.detail.labelText) {
      this.activeLabel.textContent = e.detail.labelText;
      
      // Find and select the matching option
      Array.from(this.dropdown.options).forEach(option => {
        if (option.textContent === e.detail.labelText) {
          this.dropdown.value = option.value;
        }
      });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Initialize any size dropdowns on the page
  window.initializeSizeDropdowns = function() {
    const dropdownConfigs = document.querySelectorAll('[data-size-dropdown]');
    
    dropdownConfigs.forEach(config => {
      new SizeDropdown({
        containerId: config.id,
        dropdownId: `${config.id}-select`,
        labelText: config.getAttribute('data-label-text') || 'Size',
        defaultText: config.getAttribute('data-default-text') || 'Select a size',
        onSizeChange: function(value, text) {
          // Optional: dispatch a custom event for other components
          document.dispatchEvent(new CustomEvent('sizeDropdownChanged', {
            detail: { value, text, sourceId: config.id }
          }));
        }
      });
    });
  };
  
  // Run initialization
  window.initializeSizeDropdowns();
});